// SeatMapPage — Seat selection step of the booking funnel.
// 6-col × 28-row grid. Premium rows 1–4 (₹2,500), Exit rows 13–14 (₹1,500), Standard free.
// TODO(booking-API-PR): wire to GET /flights/:offerId/seatmap + PATCH /bookings/:id (seat).

import { useNavigate } from 'react-router-dom';
import { BookingProgressBar } from '@/components/BookingProgressBar';
import { SeatGrid } from '@/components/SeatGrid';
import { PriceBreakdown } from '@/components/PriceBreakdown';
import { Button } from '@/components/primitives';
import { BookingProvider, useBooking } from '@/lib/booking-store';
import { useIsMobile } from '@/hooks/useIsMobile';
import {
  MOCK_FLIGHT,
  getSeatTier,
  getSeatUpgradePaise,
  formatPaise,
} from '@/lib/booking-mock';

// ─── LEGEND ITEM ─────────────────────────────────────────────────────────────
function LegendItem({
  bg,
  border,
  label,
}: {
  bg:     string;
  border: string;
  label:  string;
}) {
  return (
    <div className="flex items-center gap-2">
      <div
        className="w-5 h-4 rounded"
        style={{ background: bg, border: `1.5px solid ${border}` }}
      />
      <span
        className="text-[12px] text-[#8C98B0]"
        style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
      >
        {label}
      </span>
    </div>
  );
}

// ─── INNER ────────────────────────────────────────────────────────────────────
function SeatMapInner() {
  const navigate            = useNavigate();
  const isMobile            = useIsMobile();
  const { state, dispatch } = useBooking();
  const f                   = MOCK_FLIGHT;
  const selectedSeat        = state.selectedSeat;

  const selRow           = selectedSeat ? parseInt(selectedSeat, 10) : null;
  const seatTier         = selRow ? getSeatTier(selRow) : null;
  const seatUpgradePaise = selRow ? getSeatUpgradePaise(selRow) : 0;

  function handleSelect(seat: string | null) {
    dispatch({ type: 'SET_SEAT', seat });
  }

  function handleContinue() {
    navigate('/payment');
  }

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <BookingProgressBar step="seats" />

      <div style={{ padding: '32px clamp(16px, 4vw, 64px) 80px', maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 340px', gap: 28, alignItems: 'start' }}>

          {/* ── Left: seat map ────────────────────────────────────────────── */}
          <div>
            <h2
              className="text-[28px] font-semibold text-[#F4F3F0] mb-1.5 tracking-tight"
              style={{ fontFamily: '"Fraunces Variable","Fraunces",Georgia,serif', letterSpacing: '-0.02em' }}
            >
              Choose your seat
            </h2>
            <p
              className="text-[14px] text-[#8C98B0] mb-5"
              style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
            >
              {f.airline} {f.flightNo} · Boeing 787-9 · Economy
            </p>

            {/* Legend */}
            <div className="flex flex-wrap gap-4 mb-6">
              <LegendItem
                bg="#1A2540"
                border="rgba(255,255,255,0.07)"
                label="Available"
              />
              <LegendItem
                bg="#1E1508"
                border="rgba(245,166,35,0.35)"
                label={`Premium (+${formatPaise(208000)})`}
              />
              <LegendItem
                bg="#071520"
                border="rgba(75,142,219,0.35)"
                label={`Exit row (+${formatPaise(124800)})`}
              />
              <LegendItem
                bg="#FF5A3C"
                border="#FF5A3C"
                label="Selected"
              />
              <LegendItem
                bg="#0F1628"
                border="rgba(255,255,255,0.04)"
                label="Occupied"
              />
            </div>

            <SeatGrid selected={selectedSeat} onSelect={handleSelect} />
          </div>

          {/* ── Right sidebar ─────────────────────────────────────────────── */}
          <div className="sticky top-[64px] self-start flex flex-col gap-3">
            {/* Seat info card */}
            <div className="bg-[#131C30] rounded-[24px] border border-white/[0.07] p-6">
              {selectedSeat ? (
                <>
                  <div
                    className="text-[11px] font-bold text-[#FF5A3C] tracking-[1.5px] mb-3"
                    style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                  >
                    ✓ SEAT SELECTED
                  </div>
                  <div
                    className="text-[52px] font-bold text-[#F4F3F0] leading-none tracking-tight mb-2"
                    style={{ fontFamily: '"Fraunces Variable","Fraunces",Georgia,serif' }}
                  >
                    {selectedSeat}
                  </div>
                  <div
                    className="text-[13px] text-[#8C98B0] mb-3"
                    style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                  >
                    Row {selRow},{' '}
                    {selectedSeat.slice(-1) <= 'C' ? 'left side' : 'right side'} ·{' '}
                    {seatTier === 'premium'
                      ? 'Premium economy'
                      : seatTier === 'exit'
                      ? 'Exit row'
                      : 'Economy'}
                  </div>

                  {seatUpgradePaise > 0 && (
                    <div
                      className="px-3 py-2 rounded-[10px] mb-4"
                      style={{
                        background: 'rgba(255,90,60,0.10)',
                        border: '1px solid rgba(255,90,60,0.25)',
                      }}
                    >
                      <span
                        className="text-[13px] font-bold text-[#FF5A3C]"
                        style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                      >
                        + {formatPaise(seatUpgradePaise)} seat fee
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div
                    className="text-[16px] font-bold text-[#F4F3F0] mb-2"
                    style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                  >
                    No seat selected
                  </div>
                  <p
                    className="text-[13px] text-[#8C98B0] mb-5"
                    style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                  >
                    Click any available seat on the map to choose your spot.
                  </p>
                </>
              )}

              {selectedSeat ? (
                <PriceBreakdown
                  fareLabel={`${f.airline} · ${state.selectedFare.label}`}
                  routeLabel={`${f.from} → ${f.to} · Mar 17`}
                  fareBasePaise={state.selectedFare.pricePaise}
                  seatUpgradePaise={seatUpgradePaise}
                  cta={
                    <Button
                      variant="flame"
                      size="lg"
                      full
                      onClick={handleContinue}
                      className="rounded-[14px] font-bold"
                    >
                      Continue to payment →
                    </Button>
                  }
                />
              ) : (
                <Button
                  variant="ghost"
                  size="lg"
                  full
                  onClick={handleContinue}
                  className="rounded-[14px]"
                >
                  Skip seat selection
                </Button>
              )}
            </div>

            {/* Flight mini-card */}
            <div className="bg-[#131C30] rounded-[16px] border border-white/[0.07] px-4 py-3.5">
              <div
                className="text-[12px] font-bold text-[#F4F3F0] mb-1"
                style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
              >
                Your flight
              </div>
              <div
                className="text-[12px] text-[#8C98B0]"
                style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
              >
                {f.airline} · {f.flightNo}
              </div>
              <div
                className="text-[12px] text-[#8C98B0]"
                style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
              >
                {f.from} → {f.to} · Mar 17 · 1 adult
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── EXPORT ──────────────────────────────────────────────────────────────────
export function SeatMapPage() {
  return (
    <BookingProvider>
      <SeatMapInner />
    </BookingProvider>
  );
}
